import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { ThemeToggle } from './ThemeToggle';
import FeedbackWidget from './FeedbackWidget';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import api from '../api/axios';
import {
  LayoutDashboard,
  MessageSquare,
  MapPin,
  FileText,
  Settings,
  LogOut,
  Key,
  Users,
  Code,
  Plus,
  FolderOpen,
  Globe,
  Menu,
  X
} from 'lucide-react';
import { PlanManagementIcon } from './icons/PlanManagementIcon';
import { cn } from '../lib/utils';

const getNavItems = (t) => [
  { icon: LayoutDashboard, label: t.dashboard, path: '/dashboard', testId: 'nav-dashboard' },
  { icon: MessageSquare, label: t.feedback, path: '/dashboard/feedback', testId: 'nav-feedback' },
  { icon: MapPin, label: t.roadmap, path: '/dashboard/roadmap', testId: 'nav-roadmap' },
  { icon: FileText, label: t.changelog, path: '/dashboard/changelog', testId: 'nav-changelog' },
  { icon: Code, label: t.widget, path: '/dashboard/widget', testId: 'nav-widget' },
  { icon: Users, label: t.users, path: '/dashboard/team', testId: 'nav-team' },
  { icon: PlanManagementIcon, label: t.planManagement || 'Plan Management', path: '/dashboard/project-settings', testId: 'nav-plan-management' },
  { icon: Settings, label: t.settings, path: '/dashboard/settings', testId: 'nav-settings' },
];

const languageNames = {
  tr: 'Türkçe',
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français'
};

export const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, projects, activeProject, switchProject, createProject, loadProjects } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { isDark } = useTheme();
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDomain, setNewProjectDomain] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showInactiveProjectModal, setShowInactiveProjectModal] = useState(false);
  const [selectedInactiveProject, setSelectedInactiveProject] = useState(null);
  const [activatingProject, setActivatingProject] = useState(false);
  
  const navItems = getNavItems(t);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const validateDomain = (domain) => {
    if (!domain.trim()) return true; // Optional
    const regex = /^[\w\-\.]+\.\w{2,}$|^\*\.[\w\-\.]+\.\w{2,}$/;
    return regex.test(domain.toLowerCase());
  };

  const handleProjectSelect = (projectId) => {
    const selected = projects.find(p => p.id === projectId);

    // Check if project is inactive
    if (selected && selected.is_active === false) {
      setSelectedInactiveProject(selected);
      setShowInactiveProjectModal(true);
    } else {
      // Project is active, proceed with switch
      switchProject(projectId);
    }
  };

  const handleActivateFromModal = async (projectId) => {
    setActivatingProject(true);
    try {
      // Try to activate the project directly from modal
      await api.put(`/projects/${projectId}/activate`);
      // Refresh projects list
      await loadProjects();
      setShowInactiveProjectModal(false);
      // Switch to the project
      switchProject(projectId);
      toast.success(t.projectActivated || 'Project activated');
    } catch (error) {
      if (error.response?.status === 400) {
        const errorDetail = error.response?.data?.detail || '';

        if (errorDetail.includes('Maximum active projects')) {
          // Plan limit reached - navigate to active projects management
          setShowInactiveProjectModal(false);
          navigate('/dashboard/project-settings?tab=active-projects');
          toast.error(t.maxActiveProjectsReached || 'Maximum active projects reached');
        } else {
          // Other 400 errors (e.g., activation failed) - just show error, don't navigate
          toast.error(errorDetail || (t.activationFailed || 'Failed to activate project'));
        }
      } else {
        toast.error(t.activationFailed || 'Failed to activate project');
      }
    } finally {
      setActivatingProject(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();

    if (!validateDomain(newProjectDomain)) {
      toast.error(t.invalidDomainFormat);
      return;
    }

    try {
      await createProject(newProjectName, newProjectDomain.toLowerCase());
      toast.success(t.projectCreated);
      setShowCreateProject(false);
      setNewProjectName('');
      setNewProjectDomain('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t.projectCreateFailed);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      {/* Mobile Menu Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      } fixed md:static w-64 h-screen md:h-auto dashboard-sidebar flex flex-col transition-transform duration-300 z-40 bg-white dark:bg-gray-900 md:bg-transparent dark:md:bg-transparent`}>
        <div className="p-4 md:p-6 border-b dark:border-gray-700 flex items-center justify-between md:block">
          <div className="flex flex-col items-center mb-0 md:mb-4">
            <img 
              src="/logo-256.webp" 
              alt="FeedPanels Logo" 
              className="w-28 md:w-32 h-28 md:h-32 object-contain mb-2"
            />
            <p className="hidden md:block text-xs text-gray-500 dark:text-gray-400 text-center truncate w-full mb-2">{user?.name}</p>
            {/* Add Project Button */}
            <Button
              variant="outline"
              className="w-full justify-center text-xs md:text-sm hidden md:flex"
              onClick={() => setShowCreateProject(true)}
              data-testid="add-project-button"
            >
              <Plus className="w-4 md:w-5 h-4 md:h-5 mr-2 flex-shrink-0" />
              <span>{t.addProject}</span>
            </Button>
          </div>
          {/* Mobile Close Button */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </button>
        </div>

        {/* Project Selector */}
        {projects.length > 0 && activeProject && (
          <div className="px-2 md:px-4 py-2 md:py-0 space-y-2">
            <Label className="text-xs text-gray-500 dark:text-gray-400 text-[11px] md:text-xs px-1">{t.activeProject}</Label>
            <Select
              value={activeProject.id}
              onValueChange={handleProjectSelect}
            >
              <SelectTrigger className="w-full text-sm md:text-base" data-testid="project-selector">
                <SelectValue>
                  <div className="flex items-center space-x-2 truncate">
                    <FolderOpen className="w-3 md:w-4 h-3 md:h-4 flex-shrink-0" />
                    <span className="truncate">{activeProject.name}</span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <nav className="flex-1 p-2 md:p-4 space-y-0.5 md:space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={cn(
                'flex items-center space-x-3 px-2 md:px-3 py-2 rounded-lg transition-colors text-sm md:text-base',
                location.pathname === item.path
                  ? 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 font-medium'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              )}
              data-testid={item.testId}
            >
              <item.icon className="w-4 md:w-5 h-4 md:h-5 flex-shrink-0" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Feedback Widget Container */}
        <div id="feedpanels-widget-container" className="my-4 relative px-2 md:px-4">
          <FeedbackWidget tenantId="03fc92f9-ef6a-49e4-9efe-c97db4e42b57" language={language} theme={isDark ? "dark" : "light"} />
        </div>

        <div className="p-2 md:p-4 border-t dark:border-gray-700 space-y-2">

          {/* Legal Links */}
          <div className="mb-2 pb-2 border-b dark:border-gray-700">
            <div className="flex flex-col space-y-1">
              <Link
                to="/legal/privacy"
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors text-left"
              >
                {t.privacyPolicy}
              </Link>
              <Link
                to="/legal/terms"
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors text-left"
              >
                {t.legalNotice}
              </Link>
            </div>
          </div>


          {/* Theme Toggle */}
          <div className="mb-2 flex items-center justify-between">
            <Label className="text-xs text-gray-500 dark:text-gray-400 text-[11px] md:text-xs">{t.theme || 'Theme'}</Label>
            <ThemeToggle />
          </div>

          {/* Language Selector */}
          <div className="mb-2">
            <Label className="text-xs text-gray-500 dark:text-gray-400 mb-2 block text-[11px] md:text-xs">{t.language || 'Language'}</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-full text-sm md:text-base" data-testid="language-selector">
                <SelectValue>
                  <div className="flex items-center space-x-2">
                    <Globe className="w-3 md:w-4 h-3 md:h-4" />
                    <span className="hidden md:inline">{languageNames[language] || languageNames.tr}</span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tr">{languageNames.tr}</SelectItem>
                <SelectItem value="en">{languageNames.en}</SelectItem>
                <SelectItem value="de">{languageNames.de}</SelectItem>
                <SelectItem value="es">{languageNames.es}</SelectItem>
                <SelectItem value="fr">{languageNames.fr}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Mobile Add Project Button */}
          <Button
            variant="outline"
            className="w-full justify-start text-xs md:text-sm md:hidden"
            onClick={() => setShowCreateProject(true)}
            data-testid="add-project-button-mobile"
          >
            <Plus className="w-4 md:w-5 h-4 md:h-5 mr-2 md:mr-3 flex-shrink-0" />
            <span>{t.addProjectMobile}</span>
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-xs md:text-sm"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-4 md:w-5 h-4 md:h-5 mr-2 md:mr-3 flex-shrink-0" />
            <span className="hidden md:inline">{t.logout}</span>
            <span className="md:hidden">{t.exitMobile}</span>
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto flex flex-col bg-gray-50 dark:bg-gray-950">
        {/* Mobile Header */}
        <div className="md:hidden sticky top-0 bg-white dark:bg-gray-900 border-b dark:border-gray-700 z-20 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
          </button>
          <img 
            src="/logo-256.webp" 
            alt="FeedPanels Logo" 
            className="w-28 h-28 object-contain"
          />
        </div>

        {/* Page Content */}
        <div className="flex-1">
          {children}
        </div>
      </main>

      {/* Create Project Dialog */}
      <Dialog open={showCreateProject} onOpenChange={setShowCreateProject}>
        <DialogContent className="w-full max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-lg md:text-xl leading-tight">{t.createNewProject}</DialogTitle>
            <DialogDescription className="text-xs md:text-sm">
              {t.addNewProject}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateProject} className="space-y-3 md:space-y-4">
            <div>
              <Label htmlFor="projectName" className="text-xs md:text-sm font-medium block mb-2">
                {t.projectName}
              </Label>
              <Input
                id="projectName"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder={t.projectNamePlaceholder}
                required
                data-testid="new-project-name-input"
                className="text-xs md:text-sm"
              />
            </div>
            <div>
              <Label htmlFor="projectDomain" className="text-xs md:text-sm font-medium block mb-2">
                {t.domain}
              </Label>
              <Input
                id="projectDomain"
                value={newProjectDomain}
                onChange={(e) => setNewProjectDomain(e.target.value)}
                placeholder={t.domainPlaceholder}
                data-testid="new-project-domain-input"
                className="text-xs md:text-sm"
              />
              <p className="text-[10px] md:text-xs text-gray-500 mt-2">
                {t.domainHint}
              </p>
            </div>
            <Button
              type="submit"
              className="w-full text-xs md:text-sm mt-2 md:mt-4"
              data-testid="create-project-submit"
            >
              {t.createProject}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inactive Project Modal */}
      <Dialog open={showInactiveProjectModal} onOpenChange={setShowInactiveProjectModal}>
        <DialogContent className="w-full max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-lg md:text-xl leading-tight text-yellow-600 dark:text-yellow-500">
              {t.inactiveProjectTitle || '⚠️ Inactive Project'}
            </DialogTitle>
            <DialogDescription className="text-xs md:text-sm mt-4">
              {t.inactiveProjectMessage || `The project "${selectedInactiveProject?.name}" is not active. You cannot use integrations for this project.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-6">
            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
              {t.inactiveProjectExplanation || 'To activate this project, go to Plan Management → Active Projects and enable it.'}
            </p>
            <div className="flex gap-2 flex-col sm:flex-row">
              <Button
                variant="outline"
                className="w-full text-xs md:text-sm"
                onClick={() => setShowInactiveProjectModal(false)}
              >
                {t.cancel || 'Cancel'}
              </Button>
              <Button
                className="w-full text-xs md:text-sm"
                onClick={() => handleActivateFromModal(selectedInactiveProject?.id)}
                disabled={activatingProject}
              >
                {activatingProject ? (t.activatingProject || 'Activating...') : (t.activate || 'Activate & Open')}
              </Button>
              <Button
                variant="secondary"
                className="w-full text-xs md:text-sm"
                onClick={() => {
                  setShowInactiveProjectModal(false);
                  navigate('/dashboard/project-settings?tab=active-projects');
                }}
              >
                {t.goToPlanManagement || 'Manage All'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};